import React, { useState } from 'react';
import {
  Box, Grid, TextField, Button, Typography, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, InputAdornment,
  alpha, useTheme
} from '@mui/material';
import { Person as PersonIcon, Add as AddIcon, Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { SectionPaper, SectionTitle } from './styledComponents';

const CustomerInformationSection = ({ invoice, customers, onUpdate, companyId, canEdit = true }) => {
  const theme = useTheme();
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');

  const handleSelectCustomer = (customer) => {
    onUpdate({
      customer_id: customer.id,
      customer_name: customer.name,
      customer_company: customer.company || '',
      billing_street: customer.street || '',
      billing_city: customer.city || '',
      billing_state: customer.state || '',
      billing_zip: customer.zip_code || '',
      billing_country: customer.country || ''
    });
    setCustomerDialogOpen(false);
  };

  const filteredCustomers = customers.filter(c => {
    if (!customerSearchTerm.trim()) return true;
    const term = customerSearchTerm.toLowerCase();
    return c.name?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term) ||
      c.phone?.includes(term) ||
      c.company?.toLowerCase().includes(term);
  });

  const inputSx = (editable = true) => ({
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
    background: 'white',
    opacity: editable ? 1 : 0.6,
    '& fieldset': { border: 'none' },
    '&:hover': editable ? {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
    } : {},
    '&.Mui-focused': editable ? {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
    } : {}
  });

  return (
    <>
      <SectionPaper sx={{ width: '100%', mt: 3 }}>
        <SectionTitle variant="h6"><PersonIcon />Customer Information</SectionTitle>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Button fullWidth variant="outlined"
              onClick={() => canEdit && setCustomerDialogOpen(true)}
              sx={{
                height: 56, borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white', justifyContent: 'flex-start', px: 3, py: 2.5,
                opacity: canEdit ? 1 : 0.3, pointerEvents: canEdit ? 'auto' : 'none',
                '&:hover': { border: `1px solid ${theme.palette.primary.main}`, boxShadow: canEdit ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}` : 'none' }
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <Box sx={{ textAlign: 'left', flex: 1 }}>
                  {invoice.customer_id ? (
                    <>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>{invoice.customer_name || 'Unknown Customer'}</Typography>
                      {invoice.customer_company && <Typography variant="caption" color="text.secondary">{invoice.customer_company}</Typography>}
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>Select Customer</Typography>
                  )}
                </Box>
              </Box>
            </Button>
          </Grid>

          {/* Billing Address */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, fontSize: '1rem', color: 'primary.main' }}>Billing Address</Typography>
            <Grid container spacing={3}>
              {[
                { label: 'Street', field: 'billing_street', xs: 12 },
                { label: 'City', field: 'billing_city', xs: 12, sm: 6 },
                { label: 'State', field: 'billing_state', xs: 12, sm: 6 },
                { label: 'Zip', field: 'billing_zip', xs: 12, sm: 6 },
                { label: 'Country', field: 'billing_country', xs: 12, sm: 6 }
              ].map(({ label, field, xs, sm }) => (
                <Grid item xs={xs} sm={sm} key={field}>
                  <TextField label={label} fullWidth value={invoice[field] || ''}
                    onChange={(e) => canEdit && onUpdate({ [field]: e.target.value })}
                    disabled={!canEdit} InputProps={{ sx: inputSx(canEdit) }} />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Shipping Address */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, fontSize: '1rem', color: 'primary.main' }}>Shipping Address</Typography>
            <Grid container spacing={3}>
              {[
                { label: 'Street', field: 'shipping_street', xs: 12 },
                { label: 'City', field: 'shipping_city', xs: 12, sm: 6 },
                { label: 'State', field: 'shipping_state', xs: 12, sm: 6 },
                { label: 'Zip', field: 'shipping_zip', xs: 12, sm: 6 },
                { label: 'Country', field: 'shipping_country', xs: 12, sm: 6 }
              ].map(({ label, field, xs, sm }) => (
                <Grid item xs={xs} sm={sm} key={field}>
                  <TextField label={label} fullWidth value={invoice[field] || ''}
                    onChange={(e) => canEdit && onUpdate({ [field]: e.target.value })}
                    disabled={!canEdit} InputProps={{ sx: inputSx(canEdit) }} />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button size="small"
                  onClick={() => {
                    if (!canEdit) return;
                    onUpdate({
                      shipping_street: invoice.billing_street,
                      shipping_city: invoice.billing_city,
                      shipping_state: invoice.billing_state,
                      shipping_zip: invoice.billing_zip,
                      shipping_country: invoice.billing_country
                    });
                  }}
                  sx={{
                    mt: 1, borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white', fontWeight: 500,
                    opacity: canEdit ? 1 : 0.3, pointerEvents: canEdit ? 'auto' : 'none',
                    '&:hover': { border: `1px solid ${theme.palette.primary.main}`, transform: canEdit ? 'translateY(-1px)' : 'none' }
                  }}>
                  Same as Billing
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SectionPaper>

      {/* Customer Selection Dialog */}
      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="md" fullWidth disableScrollLock>
        <DialogTitle sx={{
          fontWeight: 500, color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}><PersonIcon sx={{ mr: 2 }} />Select Customer</Box>
          <IconButton onClick={() => setCustomerDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <TextField fullWidth placeholder="Search customers..." value={customerSearchTerm}
              onChange={(e) => setCustomerSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }} />
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 500, width: 100 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCustomers.length === 0 ? (
                  <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}><Typography color="text.secondary">No customers found.</Typography></TableCell></TableRow>
                ) : filteredCustomers.map((customer) => (
                  <TableRow key={customer.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleSelectCustomer(customer)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{customer.name}</Typography>
                      {customer.company && <Typography variant="caption" color="text.secondary">{customer.company}</Typography>}
                    </TableCell>
                    <TableCell>
                      {customer.email && <Typography variant="body2">{customer.email}</Typography>}
                      {customer.phone && <Typography variant="caption" color="text.secondary">{customer.phone}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{[customer.street, customer.city, customer.state].filter(Boolean).join(', ') || 'No address'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); handleSelectCustomer(customer); }} sx={{ borderRadius: 2 }}>Select</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCustomerDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => window.open(`/company/${companyId}/customer-new`, '_blank')} variant="outlined" startIcon={<AddIcon />}>Add New Customer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerInformationSection;
